<template>
	<div class="result-state">
		<header class="header">
			<div class="status">
				<AppIcon :name="IconName.Check" :size="16" />
				<strong>{{ t("contactResearchPage.result.searchComplete") }}</strong>
				<span>· {{ sourceSummary }} · {{ duration }}</span>
			</div>
			<Button variant="secondary" size="m" @click="emit('newSearch')">
				<AppIcon :name="IconName.Plus" :size="16" />
				{{ t("contactResearchPage.actions.newSearch") }}
			</Button>
		</header>

		<main class="content">
			<Profile
				:initials="profileInitials"
				:name="profileName"
				:role="profileRole"
				:location="profileLocation"
				:linked-in-url="profileLinkedInUrl"
				:provider-progress="providerProgress"
			/>

			<KeyContactDetails :details="keyContactDetails" :providers="providers" />

			<SourcesBackground
				:provider-progress="providerProgress"
				:current-time="currentTime"
				:profile-bio="profileBio"
				:profile-facts="profileFacts"
				:search-input-facts="searchInputFacts"
			/>
		</main>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import KeyContactDetails from "@/components/contact-research/KeyContactDetails.vue"
import Profile from "@/components/contact-research/Profile.vue"
import SourcesBackground from "@/components/contact-research/SourcesBackground.vue"
import { IconName } from "@/components/icons/registry"
import {
	ContactResearchInformationField,
	ContactResearchProviderId,
	ContactResearchProviderStatus,
	type ContactResearchContact,
	type ContactResearchInput,
	type ContactResearchProviderInfo,
	type ContactResearchProviderProgress,
	type ContactResearchResult,
} from "@/types/contactResearch"

type ContactFact = {
	label: string
	value: string
	href?: string
	external?: boolean
}

type KeyContactDetail = {
	type: ContactDetailType
	value: string
	sources: string[]
	providerCount: number
	bestMatch: boolean
	indicator: ContactDetailIndicator
	priority: number
}

enum ContactDetailType {
	Mobile = "mobile",
	Email = "email",
	LinkedIn = "linkedIn",
	Phone = "phone",
}

enum ContactDetailIndicator {
	Green = "green",
	Grey = "grey",
	Blue = "blue",
}

const props = defineProps<{
	input: ContactResearchInput
	result: ContactResearchResult
	providerProgress: ContactResearchProviderProgress[]
	providers: ContactResearchProviderInfo[]
	currentTime: number
	runStartedAt: string | null
}>()

const emit = defineEmits<{
	newSearch: []
}>()

const { t } = useI18n()

const providerDisplayOrder = [
	ContactResearchProviderId.Hunter,
	ContactResearchProviderId.Canada411,
	ContactResearchProviderId.Surfe,
	ContactResearchProviderId.WebSearch,
]
const displayedProviders = computed(() =>
	[...props.providers].sort(
		(firstProvider, secondProvider) =>
			providerDisplayOrder.indexOf(firstProvider.id) - providerDisplayOrder.indexOf(secondProvider.id)
	)
)
const searchSubject = computed(() => {
	const isLinkedInOnlySearch =
		props.providerProgress.length === 1 &&
		props.providerProgress[0]?.provider.id === ContactResearchProviderId.Surfe &&
		Boolean(props.input.contactLinkedInUrl?.trim())

	return isLinkedInOnlySearch
		? props.input.contactLinkedInUrl?.trim() ?? ""
		: `${props.input.firstName} ${props.input.lastName}`.trim()
})
const subjectLocation = computed(() =>
	[props.input.contactMunicipality, props.input.contactProvince]
		.map(value => value?.trim())
		.filter(Boolean)
		.join(", ")
)
const profileContact = computed(
	() =>
		[...props.result.sanitizedContacts].sort(
			(firstContact, secondContact) => contactScore(secondContact) - contactScore(firstContact)
		)[0]
)
const profileName = computed(() => profileContact.value?.fullName || searchSubject.value)

const profileInitials = computed(() =>
	profileName.value
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(name => name[0]?.toLocaleUpperCase())
		.join("")
)

const profileRole = computed(() => {
	const role = props.result.sanitizedContacts.find(contact => contact.jobTitle)?.jobTitle
	const company =
		props.result.sanitizedContacts.find(contact => contact.companyName)?.companyName || props.input.companyName

	return [role, company].filter(Boolean).join(" · ") || t("contactResearchPage.matches.noSummary")
})

const profileLocation = computed(
	() => props.result.sanitizedContacts.find(contact => contact.location)?.location || subjectLocation.value
)

const profileLinkedInUrl = computed(
	() => props.result.sanitizedContacts.find(contact => contact.linkedInUrl)?.linkedInUrl ?? undefined
)

const successfulProviderCount = computed(
	() => props.providerProgress.filter(progress => progress.status === ContactResearchProviderStatus.Success).length
)

const sourceSummary = computed(() =>
	t("contactResearchPage.result.returnedSources", {
		count: successfulProviderCount.value,
		total: props.providerProgress.length,
	})
)

const duration = computed(() => {
	if (!props.runStartedAt) {
		return formatDuration(0)
	}

	return formatDuration(new Date(props.result.completedAt).getTime() - new Date(props.runStartedAt).getTime())
})

const profileBio = computed(() =>
	[
		...new Set(
			props.result.sanitizedContacts.flatMap(contact =>
				(contact.additionalInfos ?? []).map(additionalInfo => additionalInfo.text)
			)
		),
	].join(" ")
)

const profileFacts = computed<ContactFact[]>(() => {
	const facts = [
		{
			label: t("contactResearchPage.result.currentRole"),
			value: props.result.sanitizedContacts.find(contact => contact.jobTitle)?.jobTitle,
		},
		{
			label: t("contactResearchPage.matches.fields.companyName"),
			value:
				props.result.sanitizedContacts.find(contact => contact.companyName)?.companyName || props.input.companyName,
		},
		{ label: t("contactResearchPage.matches.fields.location"), value: profileLocation.value },
	]

	return facts.filter((fact): fact is ContactFact => Boolean(fact.value))
})

const keyContactDetails = computed(() => buildKeyContactDetails())

const searchInputFacts = computed<ContactFact[]>(() => {
	const submittedInput = props.result.input
	const facts = [
		{ label: t("contactResearchPage.form.firstName"), value: submittedInput.firstName },
		{ label: t("contactResearchPage.form.lastName"), value: submittedInput.lastName },
		{ label: t("contactResearchPage.form.companyName"), value: submittedInput.companyName },
		{ label: t("contactResearchPage.form.companyDomain"), value: submittedInput.companyDomain },
		{ label: t("contactResearchPage.form.companyAddress"), value: submittedInput.companyAddress },
		{ label: t("contactResearchPage.form.companyPhone"), value: submittedInput.companyPhone },
		{ label: t("contactResearchPage.form.companyNeq"), value: submittedInput.companyNeq },
		{ label: t("contactResearchPage.form.municipality"), value: submittedInput.contactMunicipality },
		{ label: t("contactResearchPage.form.province"), value: submittedInput.contactProvince },
		{ label: t("contactResearchPage.form.postalCode"), value: submittedInput.contactPostalCode },
		{ label: t("contactResearchPage.form.address"), value: submittedInput.contactAddress },
		{
			label: t("contactResearchPage.form.linkedInUrl"),
			value: submittedInput.contactLinkedInUrl,
			href: submittedInput.contactLinkedInUrl ? linkedInHref(submittedInput.contactLinkedInUrl) : undefined,
		},
	]
	const submittedFacts = facts.filter((fact): fact is ContactFact => Boolean(fact.value?.trim()))

	return [
		...submittedFacts,
		{
			label: t("contactResearchPage.result.sourcesRun"),
			value: displayedProviders.value
				.filter(provider => props.providerProgress.some(progress => progress.provider.id === provider.id))
				.map(provider => provider.label)
				.join(", "),
		},
	]
})

function formatDuration(durationMs: number): string {
	const seconds = Math.max(0, Math.floor(durationMs / 1000))
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	if (minutes > 0) {
		return `${minutes}m ${remainingSeconds}s`
	}

	return `${seconds}s`
}

function contactScore(contact: ContactResearchContact): number {
	return [
		contact.jobTitle,
		contact.companyName,
		contact.location,
		contact.linkedInUrl,
		...(contact.emails ?? []),
		...(contact.phones ?? []),
		...(contact.companyPhones ?? []),
	].filter(Boolean).length
}

function linkedInHref(value: string): string {
	if (/^https?:\/\//.test(value)) {
		return value
	}

	if (value.startsWith("/")) {
		return `https://www.linkedin.com${value}`
	}

	return `https://${value}`
}

function normalizeDetailValue(type: ContactDetailType, value: string): string {
	if (type === ContactDetailType.Mobile || type === ContactDetailType.Phone) {
		return value.replace(/\D/g, "")
	}

	return value.trim().toLocaleLowerCase().replace(/\/$/, "")
}

function providerInformationPriority(providerId: ContactResearchProviderId): number {
	if (providerId === ContactResearchProviderId.Surfe || providerId === ContactResearchProviderId.Hunter) {
		return 0
	}

	if (providerId === ContactResearchProviderId.WebSearch) {
		return 1
	}

	return 2
}

function detailIndicator(type: ContactDetailType, providers: Set<ContactResearchProviderId>): ContactDetailIndicator {
	if (type === ContactDetailType.LinkedIn) {
		return ContactDetailIndicator.Blue
	}

	if (providers.has(ContactResearchProviderId.Surfe) || providers.has(ContactResearchProviderId.Hunter)) {
		return ContactDetailIndicator.Green
	}

	return ContactDetailIndicator.Grey
}

function providerLabel(providerId: ContactResearchProviderId): string {
	return props.providers.find(provider => provider.id === providerId)?.label ?? providerId
}

function buildKeyContactDetails(): KeyContactDetail[] {
	const details = new Map<
		string,
		{
			type: ContactDetailType
			value: string
			providers: Set<ContactResearchProviderId>
			sourceUrls: Set<string>
		}
	>()

	function addDetail(
		type: ContactDetailType,
		value: string,
		provider: ContactResearchProviderId,
		contact: ContactResearchContact,
		informationField?: ContactResearchInformationField
	): void {
		const key = `${type}:${normalizeDetailValue(type, value)}`

		const detail = details.get(key) ?? {
			type,
			value,
			providers: new Set<ContactResearchProviderId>(),
			sourceUrls: new Set<string>(),
		}
		detail.providers.add(provider)

		if (informationField) {
			for (const information of contact.informationFound ?? []) {
				if (information.field === informationField) {
					detail.sourceUrls.add(sourceLabel(information.sourceUrl))
				}
			}
		}

		details.set(key, detail)
	}

	for (const progress of props.providerProgress) {
		for (const contact of progress.result?.sanitizedContacts ?? []) {
			for (const phone of contact.phones ?? []) {
				addDetail(ContactDetailType.Mobile, phone, progress.provider.id, contact, ContactResearchInformationField.Phone)
			}

			for (const email of contact.emails ?? []) {
				addDetail(ContactDetailType.Email, email, progress.provider.id, contact, ContactResearchInformationField.Email)
			}

			if (contact.linkedInUrl) {
				addDetail(ContactDetailType.LinkedIn, contact.linkedInUrl, progress.provider.id, contact)
			}

			for (const phone of contact.companyPhones ?? []) {
				addDetail(
					ContactDetailType.Phone,
					phone,
					progress.provider.id,
					contact,
					ContactResearchInformationField.CompanyPhone
				)
			}
		}
	}

	return [...details.values()]
		.map(detail => {
			const providers = [...detail.providers].sort(
				(firstProvider, secondProvider) =>
					providerInformationPriority(firstProvider) - providerInformationPriority(secondProvider)
			)

			return {
				type: detail.type,
				value: detail.value,
				sources: [...providers.map(providerLabel), ...detail.sourceUrls],
				providerCount: detail.providers.size,
				bestMatch: detail.providers.size > 1,
				indicator: detailIndicator(detail.type, detail.providers),
				priority: Math.min(...providers.map(providerInformationPriority)),
			}
		})
		.sort(
			(firstDetail, secondDetail) =>
				firstDetail.priority - secondDetail.priority || secondDetail.providerCount - firstDetail.providerCount
		)
}

function sourceLabel(sourceUrl: string): string {
	try {
		const url = new URL(sourceUrl)

		return `${url.hostname.replace(/^www\./, "")}${url.pathname === "/" ? "" : url.pathname}`
	} catch {
		return sourceUrl
	}
}
</script>

<style scoped>
.header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--space-16);
	margin-bottom: var(--space-18);

	:deep(.btn) {
		gap: var(--space-8);
	}
}

.status {
	display: flex;
	align-items: center;
	gap: var(--space-8);
	font-size: var(--font-size-small);

	svg {
		color: var(--accent-success);
	}

	span {
		color: var(--neutral-600);
	}
}

.content {
	display: grid;
	gap: var(--space-24);
}

@media (max-width: 640px) {
	.header {
		align-items: flex-start;
		flex-direction: column;
	}
}
</style>
